import { Company as CompanyProfile } from '@mercator-gcp/types/backend/Company';
import { ProjectCompany } from '@mercator-gcp/types/backend/CompanyInProject';
import { useQuery } from 'react-query';
import { useGet } from 'hooks/useGet';
import CompaniesAPI from 'services/API/CompaniesAPI';
import ElasticSearchFilterAPI from 'services/API/ElasticSearchFilterAPI';
import { Employee } from 'types';
import type { ProjectFilterRequest } from '@mercator-gcp/types/elastic_search_service/ProjectFilterRequest';

export const useCompany = (id: string) => {
  return useGet<CompanyProfile>(`/v3/companies/${id}/`, [id]);
};

export const useCompanyProjectsV2 = (
  id: number[],
  page: number = 1,
  sortArray: string[] = [],
  mercatorMetropolitanIds?: string[],
  status?: string,
  keywords?: ProjectFilterRequest['keywords'],
) => {
  const companyProjects = useQuery(
    [
      'company-projects-fetch',
      id,
      sortArray,
      page,
      mercatorMetropolitanIds,
      status,
      keywords,
    ],
    async () => {
      const result = await ElasticSearchFilterAPI.postFilterProjectsAndDocuments({
        companies: id,
        sort: sortArray,
        page,
        mercator_metropolitan_ids: mercatorMetropolitanIds,
        status,
        keywords,
      });

      return result?.data;
    },
    { refetchOnWindowFocus: false },
  );

  return companyProjects;
};

type involvement = {
  name: string;
  count: number;
  uuid: string;
};

type InvolvementsResponse = involvement[];

export type InvolvedResponse = ProjectCompany[];

type ContactResponse = {
  count: number;
  results: Employee[];
};

export const useCompanyContacts = (id: string, page: number = 1) => {
  return useQuery(
    ['get-company-contacts', id, page],
    async (): Promise<ContactResponse | null> => {
      const result = await CompaniesAPI.getCompanyContactsPage(id, page);

      return result?.data || null;
    },
    { refetchOnWindowFocus: false },
  );
};

export const useCompanyInvolvement = (id: string) => {
  return useQuery(
    ['get-commonly-works-with', id],
    async (): Promise<InvolvementsResponse> => {
      const result = await CompaniesAPI.getCompanyInvolvement(id);

      return result?.data || [];
    },
    { refetchOnWindowFocus: false },
  );
};

export const useCompaniesInvolved = (id?: string) => {
  return useQuery(
    ['get-companies-invoked-in-project', id],
    async (): Promise<InvolvedResponse> => {
      const result = await CompaniesAPI.getCompaniesInvolvedInProject(id!);

      return result?.data || [];
    },
    { refetchOnWindowFocus: false },
  );
};

type ProjectCityCounts = { city: string; count: number }[];

export const useCompanyProjectCityCounts = (id: string) => {
  return useQuery(
    ['get-company-project-city-counts', id],
    async (): Promise<ProjectCityCounts | null> => {
      const result = await CompaniesAPI.getCompanyProjectCityCounts(id);

      return result?.data || null;
    },
    { refetchOnWindowFocus: false },
  );
};
